import type { Message } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, type ReactNode } from "react";

interface ContactFormMessageCardProps {
  message: Message & { channel: "contactForm" };
  isLast?: boolean;
}

export const ContactFormMessageCard = memo(function ContactFormMessageCard({
  message,
  isLast,
}: ContactFormMessageCardProps) {
  const data = message.channelData;

  return (
    <Flex
      bgColor="primary"
      borderBottomWidth="1px"
      borderColor="primary"
      borderLeftWidth="1px"
      borderRightWidth="1px"
      borderStyle="solid"
      bottomLeftRadius={isLast ? "sm" : undefined}
      bottomRightRadius={isLast ? "sm" : undefined}
      dir="column"
      gap="lg"
      p="xl"
      w="full"
    >
      <Flex dir="column" gap="xs">
        <Text fontSize="lg" fontWeight="semibold">
          Contact form submission
        </Text>
        <Text fontSize="sm" textColor="tertiary">
          {formatTimestamp(message.sentAt)}
        </Text>
      </Flex>

      <Divider />

      <Flex dir="column" gap="md">
        {data?.name && <Field label="Name" value={data.name} />}
        {data && <Field label="Email" value={data.email} />}
        {data?.phone && <Field label="Phone" value={data.phone} />}
        {data?.subject && <Field label="Subject" value={data.subject} />}
        <Field label="Comment" multiline value={message.content ?? ""} />
        {data?.customFields.map((field, index) => (
          <Field
            key={`${field.label}-${index}`}
            label={field.label}
            value={field.value}
          />
        ))}
      </Flex>

      {message.attachments.length > 0 && (
        <>
          <Divider />
          <Flex dir="column" gap="sm">
            <Text fontSize="sm" fontWeight="semibold" textColor="secondary">
              Attachments
            </Text>
            <Flex dir="column" gap="xs">
              {message.attachments.map((attachment) => (
                <AttachmentRow
                  description={
                    attachment.description._tag === "Some"
                      ? attachment.description.value
                      : "Attachment"
                  }
                  key={attachment.id}
                  mimeType={
                    attachment.mimeType._tag === "Some"
                      ? attachment.mimeType.value
                      : null
                  }
                  url={attachment.url}
                />
              ))}
            </Flex>
          </Flex>
        </>
      )}
    </Flex>
  );
});

const Field = memo(function Field({
  label,
  value,
  multiline,
}: {
  label: string;
  value: string;
  multiline?: boolean;
}) {
  return (
    <Flex dir="column" gap="xxs">
      <Text fontSize="sm" fontWeight="semibold" textColor="primary">
        {label}
      </Text>
      <Text
        fontSize="sm"
        style={
          multiline
            ? { whiteSpace: "pre-wrap", wordBreak: "break-word" }
            : { wordBreak: "break-word" }
        }
        textColor="primary"
      >
        {value}
      </Text>
    </Flex>
  );
});

const AttachmentRow = memo(function AttachmentRow({
  description,
  mimeType,
  url,
}: {
  description: string;
  mimeType: string | null;
  url: string;
}): ReactNode {
  const isImage = mimeType?.startsWith("image/");

  if (isImage) {
    return (
      <a
        href={url}
        rel="noopener noreferrer"
        style={{ textDecoration: "none" }}
        target="_blank"
      >
        <img
          alt={description}
          src={url}
          style={{
            maxWidth: "300px",
            maxHeight: "300px",
            objectFit: "contain",
            borderRadius: "8px",
          }}
        />
      </a>
    );
  }

  return (
    <Flex
      align="center"
      bgColor="tertiary"
      gap="sm"
      justify="space-between"
      p="md"
      radius="md"
    >
      <Text fontSize="sm" textColor="primary">
        {description}
      </Text>
      <a
        href={url}
        rel="noopener noreferrer"
        style={{ color: "inherit" }}
        target="_blank"
      >
        <Text fontSize="xs" textColor="secondary">
          Download
        </Text>
      </a>
    </Flex>
  );
});

function formatTimestamp(date: Date): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
  }).format(date);
}
